/**
 * TypeScript types for workout data structures
 * Must match backend JSON format exactly.
 *
 * Days come in two shapes from the backend:
 *   - Sessions-based: { day_name, category, sessions: Session[] }
 *   - Single-session: { day_name, name, goal, exercises: Exercise[], ... }
 * Use `getDaySessions(day)` to read either shape uniformly.
 */

export interface Exercise {
  exercise: string;
  sets: number;
  reps: string;
  weight: number; // in lbs
}

export interface Session {
  category?: string; // sport/movement category, e.g. "Walk", "Lift", "Rest"
  name?: string;     // e.g., "Upper Body Strength"
  goal?: string;
  priorities?: string[];
  reason?: string;
  exercises?: Exercise[];
  rest_day?: boolean; // session-level rest marker (Rest category sessions)
}

export interface RestDay {
  rest_day: true;
  day_name?: string;
  category?: string;     // typically "Rest"
  sessions?: Session[];  // optional — some payloads carry a single Rest session
}

export interface WorkoutDay {
  day_name?: string;
  category?: string;       // present on the sessions-based shape
  name?: string;           // single-session shape / first-session name
  goal?: string;           // single-session shape / first-session goal
  priorities?: string[];
  reason?: string;
  exercises?: Exercise[];  // single-session shape (may be empty when sessions exist)
  sessions?: Session[];    // sessions-based shape — one or more sessions
}

export type DayData = WorkoutDay | RestDay;

export interface Week {
  goal: string;
  priorities: string[];
  reason: string;
  days: Record<string, DayData>; // ISO date strings (e.g., "2026-02-16") as keys
}

export interface Cycle {
  goal: string;
  priorities: string[];
  reason: string;
  weeks: Week[];
}

export interface Season {
  goal: string;
  priorities: string[];
  cycles: Cycle[];
}

export interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  age?: number;
  gender?: string;
  location?: string;
  height_feet?: number;
  height_inches?: number;
  weight_lbs?: number;
  selected_activities?: string[];
  training_schedule?: Record<string, number>;
  preferred_days?: Record<string, string[]>;
  goals?: string[] | { name: string }[];
  onboarding_data?: Record<string, any>;
  profile_summary?: string;
}

// Type guards and shape helpers

export function isRestDay(day: DayData): day is RestDay {
  return 'rest_day' in day && (day as RestDay).rest_day === true;
}

export function isWorkoutDay(day: DayData): day is WorkoutDay {
  return !isRestDay(day);
}

/**
 * Return a normalized list of sessions for a day, whichever shape the backend
 * sent.
 *
 * - sessions-based: returns `day.sessions` (filtered: drop session-level rest markers)
 * - single-session: synthesizes one Session from the day's top-level fields
 * - rest day:       returns []
 */
export function getDaySessions(day: DayData | undefined | null): Session[] {
  if (!day) return [];
  if (isRestDay(day)) return [];
  const w = day as WorkoutDay;
  if (Array.isArray(w.sessions) && w.sessions.length > 0) {
    return w.sessions.filter((s) => s.rest_day !== true);
  }
  if (Array.isArray(w.exercises)) {
    return [{
      category: w.category,
      name: w.name,
      goal: w.goal,
      priorities: w.priorities,
      reason: w.reason,
      exercises: w.exercises,
    }];
  }
  return [];
}

/** True when at least one session has a non-empty exercises list. */
export function dayHasExercises(day: DayData | undefined | null): boolean {
  return getDaySessions(day).some((s) => Array.isArray(s.exercises) && s.exercises.length > 0);
}

/** Return the high-level goal text for a day, in either shape. */
export function getDayGoal(day: DayData | undefined | null): string {
  if (!day || isRestDay(day)) return '';
  const w = day as WorkoutDay;
  if (typeof w.goal === 'string' && w.goal) return w.goal;
  const sessions = getDaySessions(day);
  return sessions[0]?.goal ?? '';
}

/** Return a short label for the day (category or name). */
export function getDayLabel(day: DayData | undefined | null): string {
  if (!day) return '';
  if (isRestDay(day)) return 'Rest';
  const w = day as WorkoutDay;
  if (w.category) return w.category;
  if (w.name) return w.name;
  const s = getDaySessions(day)[0];
  return s?.category || s?.name || 'Workout';
}

/** Walk every week across every cycle, returning a flat array of {cycleIndex, weekIndex, week}. */
export function walkSeasonWeeks(season: Season | null | undefined): Array<{ cycleIndex: number; weekIndex: number; week: Week }> {
  if (!season) return [];
  const out: Array<{ cycleIndex: number; weekIndex: number; week: Week }> = [];
  season.cycles.forEach((c, ci) => {
    c.weeks.forEach((w, wi) => out.push({ cycleIndex: ci, weekIndex: wi, week: w }));
  });
  return out;
}

// Constructors

export function createEmptyWorkoutDay(): WorkoutDay {
  return {
    name: 'New Workout',
    goal: '',
    priorities: [],
    reason: '',
    exercises: [],
    sessions: [{ category: 'Workout', name: 'New Workout', goal: '', exercises: [] }],
  };
}

export function createRestDay(): RestDay {
  return {
    rest_day: true,
    category: 'Rest',
    sessions: [{ rest_day: true, category: 'Rest' }],
  };
}

export function createEmptyExercise(): Exercise {
  return {
    exercise: '',
    sets: 3,
    reps: '10',
    weight: 0,
  };
}
